import { NGXLogger } from 'ngx-logger';
import { catchError, map, Observable, of } from 'rxjs';
import { EventAccessApiService } from './event-access-api.service';
import { SmEventApiService } from '../sales-manager/sm-event-api.service';
import { EventApiService } from '../event/event-api.service';
import { EventResponse } from 'src/app/models/core/event.model';
import {
  ApiEventItem,
  EVENT_ACCESS_PAGE_SIZE,
  EventAccessPage,
  EventListTab,
  EventSourceEntry,
  ListingRole,
} from 'src/app/types/event-access.types';
import {
  fromMyEvent,
  fromSalesManagerEvent,
  fromSellerAppEvent,
  fromSellersPermissionEvent,
} from 'src/app/utils/event-access/event-source-mappers';

/**
 * - `my-events`: Vender + Reporte global (solo vigentes).
 * - `seller-app`: Vender en pasados/inactivos (`/seller-app/events/past|inactive`).
 * - `sellers-permissions`: Reporte global en pasados/inactivos (`/sellers/events-past|events-inactive`).
 * - `administrator` / `sales-manager`: `/sales-manager/events*` con ese rol.
 */
export type EventSourceKey = 'my-events' | 'seller-app' | 'sellers-permissions' | ListingRole;

/** Una página de una fuente ya mapeada. Si falló, viene vacía y sin más páginas. */
export interface EventSourceResult {
  readonly key: EventSourceKey;
  readonly entries: readonly EventSourceEntry[];
  readonly hasMore: boolean;
  readonly failed: boolean;
}

export interface EventSourceDeps {
  readonly accessApi: EventAccessApiService;
  readonly smApi: SmEventApiService;
  readonly eventApi: EventApiService;
  readonly logger: NGXLogger;
}

export interface ProfileAccess {
  readonly isBusinessAdmin: boolean;
  readonly isBoss: boolean;
}

export interface SourceRequest {
  readonly tab: EventListTab;
  readonly page: number;
  readonly q: string;
}

/** Fuentes que se piden según la pestaña y lo que dice `/me`. */
export function sourceKeysFor(tab: EventListTab, profile: ProfileAccess): EventSourceKey[] {
  const sellerSources: EventSourceKey[] = tab === 'active' ? ['my-events'] : ['seller-app', 'sellers-permissions'];
  return [
    ...sellerSources,
    ...(profile.isBusinessAdmin ? ['administrator' as const] : []),
    ...(profile.isBoss ? ['sales-manager' as const] : []),
  ];
}

/**
 * El modelo `Event` tipa las fechas como `Date`, pero el JSON trae strings: es la misma
 * respuesta que `ApiEventItem` describe.
 */
function asApiPage(response: EventResponse): EventAccessPage<ApiEventItem> {
  return response as unknown as EventAccessPage<ApiEventItem>;
}

function toResult<T>(
  key: EventSourceKey,
  mapper: (item: T) => EventSourceEntry,
): (page: EventAccessPage<T>) => EventSourceResult {
  return page => ({
    key,
    entries: (page.data ?? []).map(mapper),
    hasMore: !!page.meta && page.meta.current_page < page.meta.last_page,
    failed: false,
  });
}

function salesManagerListing(req: SourceRequest, role: ListingRole, deps: EventSourceDeps): Observable<EventResponse> {
  const { page, q } = req;
  switch (req.tab) {
    case 'active': return deps.smApi.getUpcomingEvents(page, EVENT_ACCESS_PAGE_SIZE, q, role);
    case 'ended': return deps.smApi.getPastEvents(page, EVENT_ACCESS_PAGE_SIZE, q, role);
    case 'inactive': return deps.smApi.getInactiveEvents(page, EVENT_ACCESS_PAGE_SIZE, q, role);
  }
}

function sellerAppListing(req: SourceRequest, deps: EventSourceDeps): Observable<EventResponse> {
  return req.tab === 'inactive'
    ? deps.eventApi.getInactiveEventsBySeller(req.page, EVENT_ACCESS_PAGE_SIZE, req.q)
    : deps.eventApi.getPastEventsBySeller(req.page, EVENT_ACCESS_PAGE_SIZE, req.q);
}

function requestSource(key: EventSourceKey, req: SourceRequest, deps: EventSourceDeps): Observable<EventSourceResult> {
  switch (key) {
    case 'my-events':
      return deps.accessApi.getMyEvents(req.page, req.q).pipe(map(toResult(key, fromMyEvent)));
    case 'sellers-permissions':
      return deps.accessApi.getSellersEventsWithPermissions(req.tab === 'inactive' ? 'inactive' : 'ended', req.page, req.q)
        .pipe(map(toResult(key, fromSellersPermissionEvent)));
    case 'seller-app':
      return sellerAppListing(req, deps).pipe(map(asApiPage), map(toResult(key, fromSellerAppEvent)));
    default:
      return salesManagerListing(req, key, deps).pipe(
        map(asApiPage),
        map(toResult(key, (event: ApiEventItem) => fromSalesManagerEvent(event, key))),
      );
  }
}

/** Pide una página de una fuente. Nunca falla: una fuente caída no tumba a las demás. */
export function fetchSource(key: EventSourceKey, req: SourceRequest, deps: EventSourceDeps): Observable<EventSourceResult> {
  return requestSource(key, req, deps).pipe(
    catchError((error: unknown) => {
      deps.logger.warn('Fuente de eventos no disponible', { key, ...req, error });
      return of<EventSourceResult>({ key, entries: [], hasMore: false, failed: true });
    }),
  );
}
